import { ArrowRight, CheckCircle, Phone, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { NAV_LINKS, HERO_CHECKS, HERO_TARIFS, FALLBACK_MOANA, scrollTo } from './moana-data';

const Eyebrow = ({ children, light }) => (
  <div className={`inline-flex items-center gap-2.5 text-xs font-semibold tracking-[0.22em] uppercase mb-3.5 ${light ? 'text-[#F59E0B]/85' : 'text-[#F59E0B]'}`}>
    <span className={`w-7 h-[1.5px] ${light ? 'bg-[#F59E0B]/85' : 'bg-[#F59E0B]'}`} />
    {children}
  </div>
);

export { Eyebrow };

export const MoanaHero = ({ cms }) => (
  <>
    {/* SubNav */}
    <div className="sticky z-40 bg-[#0F766E]/[0.97] backdrop-blur-sm border-b border-white/10 hidden md:block" style={{ top: '72px' }} data-testid="moana-subnav">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 h-14">
        <div className="flex flex-col leading-none flex-shrink-0 min-w-0">
          <span className="text-sm font-semibold text-white whitespace-nowrap"><span className="text-[#F59E0B]">AERH</span> Consulting Tahiti</span>
          <span className="text-[10px] tracking-[0.16em] uppercase text-[#F59E0B] mt-0.5 whitespace-nowrap">Moana Paye · Gestion de la paie PF</span>
        </div>
        <div className="flex items-center gap-0.5 flex-nowrap">
          {NAV_LINKS.map(l => (
            <button key={l.id} onClick={() => scrollTo(l.id)} className="text-[13px] font-medium text-white/70 px-3 py-1.5 rounded hover:text-white hover:bg-white/[0.07] transition-all whitespace-nowrap">
              {l.label}
            </button>
          ))}
          <button onClick={() => scrollTo('contact-moana')} className="ml-1 text-[13px] font-semibold bg-[#F59E0B] text-[#0F766E] px-4 py-1.5 rounded hover:bg-[#D97706] transition-all whitespace-nowrap" data-testid="nav-audit-btn">
            Audit gratuit
          </button>
          <a href="/moana-paye/login" className="ml-2 text-[13px] font-semibold bg-white/10 text-white px-4 py-1.5 rounded hover:bg-white/20 transition-all flex items-center gap-1.5 whitespace-nowrap" data-testid="nav-login-saas">
            <LogIn className="w-3.5 h-3.5" />Espace client
          </a>
        </div>
      </div>
    </div>

    {/* Hero */}
    <section className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] relative overflow-hidden" data-testid="moana-hero">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)', backgroundSize: '72px 72px' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 55% at 88% 18%, rgba(74,138,138,.14) 0%, transparent 58%), radial-gradient(ellipse 40% 40% at 5% 88%, rgba(240,150,30,.06) 0%, transparent 52%)' }} />
      <div className="relative z-10 max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 pt-20 lg:pt-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-[72px] items-end">
          <div className="lg:pb-16">
            <Eyebrow light>{cms.hero?.eyebrow || FALLBACK_MOANA.hero.eyebrow}</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-white leading-[1.08] tracking-tight mb-5" data-testid="hero-title">
              {cms.hero?.title || FALLBACK_MOANA.hero.title}
            </h1>
            <p className="text-base text-white/60 leading-relaxed max-w-[440px] mb-7">
              {cms.hero?.subtitle || FALLBACK_MOANA.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-2.5 mb-8">
              {(cms.hero?.checks || HERO_CHECKS).map(c => (
                <span key={c} className="text-[13px] font-medium text-white/80 bg-white/[0.07] border border-white/10 rounded-full px-3.5 py-1 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-[#F59E0B]" /> {c}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => scrollTo('contact-moana')} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-5" data-testid="hero-cta">
                Demander un audit gratuit <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button asChild variant="outline" className="border-white/30 text-white hover:border-[#F59E0B] hover:text-[#F59E0B] px-7 py-5">
                <a href="tel:+68987737815"><Phone className="mr-2 w-4 h-4" /> 87 73 78 15</a>
              </Button>
            </div>
            <div className="mt-4">
              <a href="/moana-paye/login" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#F59E0B] transition-colors" data-testid="hero-login-link">
                <LogIn className="w-4 h-4" />Déjà client ? Accédez à votre espace Moana Paye
              </a>
            </div>
          </div>
          <div data-testid="hero-guide">
            <div className="bg-black/20 border-t border-l border-white/[0.07] rounded-t-xl overflow-hidden">
              <div className="px-6 py-3.5 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#F59E0B] border-b border-white/[0.07]">
                Choisissez votre mode
              </div>
              {[
                { num: '01', name: 'Délégation totale', sub: 'AERH gère tout pour vous', price: 'Dès 850 F/sal./mois', anchor: 'modes' },
                { num: '02', name: 'Semi-délégation', sub: 'Vous saisissez, AERH valide', price: 'Tarif sur mesure', anchor: 'modes' },
                { num: '03', name: 'Autonomie totale', sub: 'Vous gérez avec Moana Paye', price: 'Tarif sur mesure', anchor: 'modes' },
              ].map((m, i) => (
                <button key={i} onClick={() => scrollTo(m.anchor)} className="w-full text-left px-6 py-4 border-b border-white/[0.07] last:border-0 hover:bg-white/[0.04] transition-colors group">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-[#F59E0B] tracking-wider mr-2">{m.num}</span>
                      <span className="text-sm font-bold text-white group-hover:text-[#F59E0B] transition-colors">{m.name}</span>
                      <p className="text-xs text-white/40 mt-0.5">{m.sub}</p>
                    </div>
                    <span className="text-xs text-white/50 font-medium">{m.price}</span>
                  </div>
                </button>
              ))}
              <div className="px-6 py-3 text-xs text-white/[0.38] italic leading-snug border-t border-white/[0.07]">
                CPS, CST, MO inclus dans chaque mode. Logiciel conçu à Papeete pour le droit social polynésien.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);
